import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { formatMoney, toCents } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/** Alta de un pago o de un anticipo a un subcontratista. `kind` = "payment" | "advance".
 * Si se pasa `projectId`, el pago se registra íntegro contra esa obra (sin elegir órdenes). */
export const PaymentDialog = ({ open, onOpenChange, kind = "payment", subId, projectId }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const [subSel, setSubSel] = useState(subId || "");
  const [monto, setMonto] = useState("");
  const [tipo, setTipo] = useState("efectivo");
  const [notas, setNotas] = useState("");
  const [aCuenta, setACuenta] = useState(false);
  const [asignado, setAsignado] = useState({});

  const { data: subs } = useQuery({
    queryKey: ["subs-lite"], enabled: open,
    queryFn: async () => (await api.get("/subs", { params: { page_size: 200 } })).data.items,
  });
  const { data: statement } = useQuery({
    queryKey: ["sub-statement", subSel], enabled: open && kind === "payment" && Boolean(subSel),
    queryFn: async () => (await api.get(`/payments/sub/${subSel}/statement`)).data,
  });

  const montoCents = toCents(monto);
  const totalAsignado = useMemo(() => Object.values(asignado).reduce((s, v) => s + (toCents(v) || 0), 0), [asignado]);

  const reset = () => { setSubSel(subId || ""); setMonto(""); setNotas(""); setACuenta(false); setAsignado({}); };

  const mutation = useMutation({
    mutationFn: async () => {
      if (kind === "advance") {
        return api.post("/advances", { sub_id: subSel, monto_cents: montoCents, observaciones: notas });
      }
      if (projectId) {
        return api.post("/payments", {
          sub_id: subSel, monto_cents: montoCents, tipo, observaciones: notas,
          project_allocations: [{ project_id: projectId, monto_cents: montoCents }],
        });
      }
      const allocations = Object.entries(asignado)
        .filter(([, v]) => toCents(v) > 0)
        .map(([wo_id, v]) => ({ work_order_id: wo_id, monto_cents: toCents(v) }));
      return api.post("/payments", {
        sub_id: subSel, monto_cents: montoCents, tipo, observaciones: notas,
        es_a_cuenta: aCuenta, allocations,
      });
    },
    onSuccess: () => {
      toast.success(t(kind === "advance" ? "finance.payments.advance_created" : "finance.payments.created"));
      qc.invalidateQueries({ queryKey: ["payments"] });
      qc.invalidateQueries({ queryKey: ["advances"] });
      qc.invalidateQueries({ queryKey: ["sub-statement"] });
      qc.invalidateQueries({ queryKey: ["project-detail"] });
      reset();
      onOpenChange(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || t("finance.payments.reason_required")),
  });

  const pendientes = statement?.ordenes_pendientes || [];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent data-testid={`${kind}-dialog`} className="max-h-[85vh] max-w-lg overflow-y-auto rounded-[10px]">
        <DialogHeader>
          <DialogTitle>{t(kind === "advance" ? "finance.payments.new_advance" : "finance.payments.new_payment")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-[12.5px]">{t("finance.payments.sub")}</Label>
            <Select value={subSel} onValueChange={setSubSel} disabled={Boolean(subId)}>
              <SelectTrigger data-testid={`${kind}-sub-select`}><SelectValue placeholder={t("finance.payments.choose_sub")} /></SelectTrigger>
              <SelectContent>
                {(subs || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12.5px]">{t("finance.payments.amount")}</Label>
              <Input data-testid={`${kind}-amount-input`} value={monto} onChange={(e) => setMonto(e.target.value)} placeholder="0.00" />
            </div>
            {kind === "payment" && (
              <div>
                <Label className="text-[12.5px]">{t("finance.payments.type")}</Label>
                <Select value={tipo} onValueChange={setTipo}>
                  <SelectTrigger data-testid="payment-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["efectivo", "transferencia", "cheque"].map((v) => (
                      <SelectItem key={v} value={v}>{t(`finance.payments.types.${v}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div>
            <Label className="text-[12.5px]">{t("finance.payments.notes")}</Label>
            <Textarea data-testid={`${kind}-notes-input`} value={notas} onChange={(e) => setNotas(e.target.value)} rows={2} />
          </div>

          {kind === "payment" && !projectId && (
            <>
              <label className="flex items-center gap-2 text-[13px]">
                <Checkbox data-testid="payment-on-account-checkbox" checked={aCuenta} onCheckedChange={(v) => setACuenta(Boolean(v))} />
                {t("finance.payments.on_account")}
              </label>
              {!aCuenta && subSel && (
                <div>
                  <p className="mb-1.5 text-[12.5px] font-medium text-ink">{t("finance.payments.pending_orders")}</p>
                  {pendientes.length === 0 ? (
                    <p className="text-[12.5px] text-ink-tertiary">{t("finance.payments.no_pending_orders")}</p>
                  ) : (
                    <div className="max-h-48 space-y-1.5 overflow-y-auto rounded-[8px] border border-line p-2">
                      {pendientes.map((o) => (
                        <div key={o.work_order_id} className="flex items-center justify-between gap-2 text-[12.5px]">
                          <span className="truncate">{o.code} · {o.property_nombre}</span>
                          <div className="flex items-center gap-1">
                            <span className="text-[11px] text-ink-tertiary">{formatMoney(o.pendiente_cents)}</span>
                            <Input
                              data-testid={`payment-allocate-${o.work_order_id}`}
                              className="h-7 w-20 text-[12px]"
                              value={asignado[o.work_order_id] || ""}
                              onChange={(e) => setAsignado((a) => ({ ...a, [o.work_order_id]: e.target.value }))}
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      ))}
                      <p className="pt-1 text-right text-[11.5px] text-ink-tertiary" data-testid="payment-allocated-total">
                        {t("finance.payments.allocated")}: {formatMoney(totalAsignado)} {t("finance.payments.of_total", { total: formatMoney(montoCents) })}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            data-testid={`${kind}-submit-button`}
            disabled={!subSel || montoCents <= 0 || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="bg-brand hover:bg-brand-hover"
          >
            {t(kind === "advance" ? "finance.payments.create_advance" : "finance.payments.create_payment")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
